import { FormEvent, useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { FaCircleCheck, FaSpinner, FaTriangleExclamation, FaXmark } from "react-icons/fa6";
// react-markdown (and its unified/remark/micromark dependency tree) ships as
// pure ESM. Webpack handles that fine, but CRA's default jest
// transformIgnorePatterns skips all of node_modules, so jest would choke on
// the `export` syntax — package.json's "jest".transformIgnorePatterns
// re-allows exactly that dependency closure through babel-jest. If this import
// ever starts failing under `npm test` with an unexpected-token error, that
// allowlist is what needs a new package name (JSON can't hold comments, hence
// this note living here).
import ReactMarkdown, { Components } from "react-markdown";
// GFM TABLES, AND DELIBERATELY NOTHING ELSE OUT OF GFM.
//
// The answer stage may lay a parameter/value set out as a markdown table
// (backend item O-33), and CommonMark — which is all react-markdown parses on
// its own — renders such a table as a wall of literal pipe characters. What is
// added here is the table extension ALONE, wired by hand, rather than
// `remark-gfm`.
//
// The reason is autolink literals. remark-gfm bundles them, they cannot be
// switched off through its options, and they turn any bare `www.…`,
// `https://…` or `user@host` that appears in ANSWER TEXT ECHOED OUT OF AN
// UPLOADED DOCUMENT into live, clickable DOM — a click target chosen by the
// document rather than by us. The round-3 position on document-derived markup
// is that it stays inert unless it was deliberately authored, and an explicit
// `[label](url)` link still renders exactly as it always has. Composing the
// two table packages directly means autolinks are never turned on in the first
// place, so there is nothing to disable and nothing to regress. Strikethrough,
// task lists and footnotes are excluded by the same construction.
//
// Both packages are already in the micromark/mdast dependency closure that
// package.json's "jest".transformIgnorePatterns allowlists; `markdown-table`
// (pulled in by mdast-util-gfm-table's serialising half) is the one new name
// that had to be added there.
import { gfmTable } from "micromark-extension-gfm-table";
import { gfmTableFromMarkdown } from "mdast-util-gfm-table";
import type { Processor } from "unified";
import {
    AgentAction,
    DocumentReadingMode,
    FeedbackRating,
    IngestionJob,
    SourceCitation,
    SourceLink,
    UploadResponse,
    VisualAssetReference,
    abortDirectUpload,
    clearKnowledgeSession,
    completeDirectUpload,
    fetchIngestionStatus,
    initiateDirectUpload,
    sendChatMessage,
    sendMessageFeedback,
    uploadDirectPart,
    uploadKnowledgeDocument,
} from "../../api/documentAgent";
import { toastifyConfigs } from "../../config/constants";
import BoxAnimation from "../animation/BoxAnimation";
import Header from "../theme-one/Header";
import SourcePreviewDrawer, { PreviewDocument } from "./SourcePreviewDrawer";
import InlineVisualAssetPreview from "./InlineVisualAssetPreview";

type ChatRole = "assistant" | "user";

type ChatMessage = {
    id: string;
    role: ChatRole;
    text: string;
    createdAt: Date;
    messageId?: string;
    sources?: SourceLink[];
    actions?: AgentAction[];
    // Present (non-empty) only on turns that genuinely answered from the
    // uploaded documents. Older backends omit the field entirely.
    citations?: SourceCitation[];
    visualAssets?: VisualAssetReference[];
    escalationRecommended?: boolean;
    feedback?: FeedbackRating;
};

// One row of the citations-backed Sources block: a cited document with the
// distinct pages the answer drew from.
type CitationDocumentGroup = {
    documentId: string;
    title: string;
    pages: number[];
};

function groupCitationsByDocument(citations: SourceCitation[]): CitationDocumentGroup[] {
    const groups: CitationDocumentGroup[] = [];
    const byId = new Map<string, CitationDocumentGroup>();
    for (const citation of citations) {
        let group = byId.get(citation.documentId);
        if (!group) {
            group = { documentId: citation.documentId, title: citation.documentTitle, pages: [] };
            byId.set(citation.documentId, group);
            groups.push(group);
        }
        if (typeof citation.pageNumber === "number" && !group.pages.includes(citation.pageNumber)) {
            group.pages.push(citation.pageNumber);
        }
    }
    for (const group of groups) group.pages.sort((a, b) => a - b);
    return groups;
}

type ConversationSession = {
    conversationId: string;
    createdAt: number;
};

// Discriminated union so each upload can only ever be in one well-formed
// state — no "isIndexed && isFailed" combinations to guard against.
//
// "queued" and "indexing" sit between "uploading" and the two terminal
// states, and only exist on the CURRENT (async) upload contract — a job id
// in hand, and its reading not yet finished. A legacy backend's synchronous
// 200 response skips both and goes straight from "uploading" to "indexed"/
// "failed", exactly as it always has.
//
// partsDone/partsTotal are additive to "uploading" alone, and only ever set
// on the direct-upload (large-file) path: the legacy path's single POST has
// no meaningful sub-progress to report, so those fields stay undefined for
// it and the panel falls back to its plain spinner, exactly as before.
//
// progressStage/progressDone/progressTotal are additive to "indexing" alone,
// carried straight from the poll's IngestionJob row (see reconcileWithJobs
// below) — a DESCRIPTION of how far the reading has got, may be stale by a
// moment, and never a state this component branches on the way it does
// `status`. Absent on an older deployed backend, exactly like every other
// optional field this poll can omit.
type UploadedDocumentEntry =
    | { id: string; fileName: string; status: "uploading"; partsDone?: number; partsTotal?: number }
    | { id: string; fileName: string; status: "queued"; jobId: string }
    | { id: string; fileName: string; status: "indexing"; jobId: string; progressStage?: string | null; progressDone?: number | null; progressTotal?: number | null }
    | { id: string; fileName: string; status: "indexed"; chunkCount: number | null; readingMode?: DocumentReadingMode | null; jobId?: string }
    | { id: string; fileName: string; status: "failed"; errorMessage: string; jobId?: string };

const CONVERSATION_SESSION_STORAGE_KEY = "qouda.documentChat.conversationSession";
const UPLOADED_DOCUMENTS_STORAGE_KEY = "qouda.documentChat.uploadedDocuments";
const CONVERSATION_SESSION_TTL_MS = 2 * 60 * 60 * 1000;
// Bounds the large-upload (direct-to-storage) path, not the legacy one — see
// DIRECT_UPLOAD_THRESHOLD_BYTES below for where the two paths split.
const MAX_DOCUMENT_FILE_BYTES = 2_000_000_000;
const DOCUMENT_EXTENSIONS = [
    ".pdf", ".docx", ".xlsx", ".pptx",
    ".txt", ".md", ".csv", ".tsv", ".json", ".xml",
    ".html", ".htm", ".rtf", ".odt", ".ods", ".odp", ".epub",
];
const DOCUMENT_FILE_ACCEPT = DOCUMENT_EXTENSIONS.join(",");

// react-toastify config for error toasts: everything else uses the shared
// default (auto-closes after 5s), but errors stay up until the user
// dismisses them so a failure can't silently disappear.
const errorToastConfig = { ...toastifyConfigs, autoClose: false };

function createGuid() {
    if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
        return crypto.randomUUID();
    }

    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (char) => {
        const randomValue = Math.random() * 16 | 0;
        const value = char === "x" ? randomValue : (randomValue & 0x3) | 0x8;
        return value.toString(16);
    });
}

function createWelcomeMessage(): ChatMessage {
    return {
        id: createGuid(),
        role: "assistant",
        text: "Hi! I’m Qouda’s assistant. Upload a document and ask me anything about the information in this chat.",
        createdAt: new Date(),
    };
}

function createSessionExpiredMessage(): ChatMessage {
    return {
        id: createGuid(),
        role: "assistant",
        text: "Your session expired after two hours. I’ve started a fresh session — please re-upload any documents you’d like me to reference before asking again.",
        createdAt: new Date(),
    };
}

// Shown instead of createSessionExpiredMessage() when expiry is discovered at
// send time, so the user knows their typed message was never sent (rather
// than silently vanishing) and that it's still sitting in the textbox.
function createSessionExpiredBeforeSendMessage(): ChatMessage {
    return {
        id: createGuid(),
        role: "assistant",
        text: "Your session expired and this message was not sent — your documents need to be re-uploaded. Your message is still in the box below.",
        createdAt: new Date(),
    };
}

const STALE_UPLOAD_MESSAGE = "The session expired before this document was ready — please upload it again.";
const RELOAD_INTERRUPTED_UPLOAD_MESSAGE = "The page reloaded before this upload finished — please upload the document again.";

// A file's own upload is retried in place this many times after a 429 before
// it's given up on and marked failed (so up to MAX_UPLOAD_RATE_LIMIT_RETRIES
// + 1 attempts total reach the backend for one file).
const MAX_UPLOAD_RATE_LIMIT_RETRIES = 2;
// Ceiling on any single retry wait, regardless of what the backend's
// Retry-After asked for — a misbehaving/misconfigured backend must never
// leave the panel showing "uploading" for an unbounded stretch.
const MAX_UPLOAD_RATE_LIMIT_WAIT_SECONDS = 90;

// A file at or under this size still goes through the legacy single-request
// endpoint, completely untouched — this is a routing decision, not a limit
// of its own. Above it, the file takes the initiate/parts/complete transport
// instead, entirely because the legacy request would otherwise be too big
// for the proxy and framework limits that path is deliberately pinned to.
// Deliberately equal to the backend's own shipped DirectUploadPartBytes
// default: a file just over the line is still a small, realistic number of
// parts, not one lonely oversized one.
const DIRECT_UPLOAD_THRESHOLD_BYTES = 48_000_000;
// How many parts of one large upload are ever in flight at once. Bounded
// rather than unbounded so a many-hundred-part transfer over a fast
// connection can't open hundreds of simultaneous requests against the
// browser's own per-origin connection limit (and, on the local provider,
// against DirectUploadPartPolicy's own request budget).
const DIRECT_UPLOAD_PART_CONCURRENCY = 3;
// How many times ONE part is retried in place before the whole transfer is
// given up on (so up to MAX_DIRECT_UPLOAD_PART_RETRIES + 1 attempts reach
// storage for that one part). A single flaky part must not fail a whole
// large upload the way it would if any retry budget were shared across the
// file's parts instead of scoped to each one.
const MAX_DIRECT_UPLOAD_PART_RETRIES = 2;

// How often the panel's single status poll checks on every "queued"/
// "indexing" entry at once, while any exist.
const INGESTION_POLL_INTERVAL_MS = 2500;

// Backend stage name (IngestionProgressStage.cs) -> the label this line reads
// before the "(done/total)" suffix. Mirrors those constants by their literal
// values rather than an import — the frontend has no access to that C# type
// — and deliberately covers only the stages that carry a countable total
// today. A stage this map has no wording for, or one of these three reported
// without both counts, falls through to the plain "Reading..." text below,
// exactly as an older backend that sends no progress fields at all does.
const INDEXING_PROGRESS_STAGE_LABELS: Record<string, string> = {
    reading_pages: "Reading pages",
    surveying: "Skimming the document",
    embedding: "Filing sections",
    indexing_visuals: "Indexing tables & images",
};

// The "indexing" entry's status line. Both progressDone and progressTotal
// must be present numbers for a stage to get its counted wording — a stage
// with no countable units (e.g. "extracting", "committing") reports a stage
// name but no counts, and that must read as the existing plain "Reading...",
// never as a broken "(null/null)".
function describeIndexingProgress(
    stage: string | null | undefined,
    done: number | null | undefined,
    total: number | null | undefined,
): string {
    const label = stage ? INDEXING_PROGRESS_STAGE_LABELS[stage] : undefined;
    if (label && typeof done === "number" && typeof total === "number") {
        return `${label}... (${done}/${total})`;
    }
    return "Reading...";
}

// documentAgent's RateLimitedError is detected by shape rather than
// `instanceof`: the module is fully auto-mocked in this component's own test
// suite, and an auto-mocked class's instances don't reliably satisfy
// `instanceof` against the real export a test double stands in for.
function getRateLimitRetryAfterSeconds(error: unknown): number | null {
    if (
        error instanceof Error
        && "retryAfterSeconds" in error
        && typeof (error as { retryAfterSeconds: unknown }).retryAfterSeconds === "number"
    ) {
        return (error as { retryAfterSeconds: number }).retryAfterSeconds;
    }
    return null;
}

function wait(ms: number): Promise<void> {
    return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function getErrorMessage(error: unknown, fallback: string) {
    if (error instanceof TypeError) {
        return "The chat service could not be reached. Please try again shortly.";
    }

    if (error instanceof Error && error.message) {
        return error.message;
    }

    return fallback;
}

function persistConversationSession(session: ConversationSession) {
    try {
        window.sessionStorage.setItem(CONVERSATION_SESSION_STORAGE_KEY, JSON.stringify(session));
    } catch {
        // The in-memory state still works when storage is unavailable.
    }
}

function createConversationSession(conversationId = createGuid()): ConversationSession {
    const session = { conversationId, createdAt: Date.now() };
    if (typeof window !== "undefined") persistConversationSession(session);
    return session;
}

function getInitialConversationSession(): ConversationSession {
    if (typeof window === "undefined") return createConversationSession();

    try {
        const storedSession = window.sessionStorage.getItem(CONVERSATION_SESSION_STORAGE_KEY);
        if (storedSession) {
            const parsed = JSON.parse(storedSession) as Partial<ConversationSession>;
            const createdAt = Number(parsed.createdAt);
            const conversationId = typeof parsed.conversationId === "string" ? parsed.conversationId : "";
            const isFresh = Number.isFinite(createdAt) && Date.now() - createdAt < CONVERSATION_SESSION_TTL_MS;
            if (conversationId && isFresh) return { conversationId, createdAt };
        }
    } catch {
        window.sessionStorage.removeItem(CONVERSATION_SESSION_STORAGE_KEY);
    }

    return createConversationSession();
}

function isPersistedUploadedDocumentEntry(value: unknown): value is UploadedDocumentEntry {
    if (!value || typeof value !== "object") return false;

    const entry = value as Record<string, unknown>;
    if (typeof entry.id !== "string" || typeof entry.fileName !== "string" || typeof entry.status !== "string") {
        return false;
    }

    switch (entry.status) {
        case "uploading":
            return true;
        case "queued":
        case "indexing":
            return typeof entry.jobId === "string";
        case "indexed":
            return entry.chunkCount === null || typeof entry.chunkCount === "number";
        case "failed":
            return typeof entry.errorMessage === "string";
        default:
            return false;
    }
}

function getInitialUploadedDocuments(conversationId: string): UploadedDocumentEntry[] {
    if (typeof window === "undefined") return [];

    try {
        const storedDocuments = window.sessionStorage.getItem(UPLOADED_DOCUMENTS_STORAGE_KEY);
        if (!storedDocuments) return [];

        const parsed = JSON.parse(storedDocuments) as { conversationId?: unknown; entries?: unknown };
        if (parsed.conversationId !== conversationId || !Array.isArray(parsed.entries)) return [];

        return parsed.entries
            .filter(isPersistedUploadedDocumentEntry)
            .map((entry): UploadedDocumentEntry => entry.status === "uploading"
                ? { id: entry.id, fileName: entry.fileName, status: "failed", errorMessage: RELOAD_INTERRUPTED_UPLOAD_MESSAGE }
                : entry);
    } catch {
        try {
            window.sessionStorage.removeItem(UPLOADED_DOCUMENTS_STORAGE_KEY);
        } catch {
            // Storage is unavailable; the in-memory panel still works.
        }
        return [];
    }
}

function persistUploadedDocuments(conversationId: string, entries: UploadedDocumentEntry[]) {
    try {
        window.sessionStorage.setItem(UPLOADED_DOCUMENTS_STORAGE_KEY, JSON.stringify({ conversationId, entries }));
    } catch {
        // The in-memory panel still works when storage is unavailable.
    }
}

// Single source of truth for "has this session timed out" — both the
// background timer and the send-message guard call this instead of each
// keeping their own copy of the TTL math.
function isSessionExpired(session: ConversationSession) {
    return Date.now() - session.createdAt >= CONVERSATION_SESSION_TTL_MS;
}

// A remark plugin registers the two halves a markdown extension needs — the
// tokenizer (micromark) and the syntax-tree builder (mdast) — on the
// processor's own data namespace. `unified` is listed as an explicit
// dependency because this line imports from it; it was already installed
// underneath react-markdown, so the install is unchanged and only the
// declaration became honest.
function remarkTablesOnly(this: Processor) {
    // `unified`'s own `Data` declares these two slots only when `remark-parse`'s
    // type augmentation is in scope, and react-markdown does not pull it in.
    // The cast names exactly what is being written and nothing else.
    const data = this.data() as {
        micromarkExtensions?: unknown[];
        fromMarkdownExtensions?: unknown[];
    };
    // Both are FACTORIES, not extension objects — pushing the functions
    // themselves parses as valid JavaScript and silently disables the feature.
    (data.micromarkExtensions ??= []).push(gfmTable());
    (data.fromMarkdownExtensions ??= []).push(gfmTableFromMarkdown());
}

// Declared at module scope, like the component map below, so the plugin list
// keeps a stable identity across renders.
const assistantRemarkPlugins = [remarkTablesOnly];

// Element overrides that map the answer model's markdown onto the assistant
// bubble's existing typography. Declared once at module scope, not inline in
// the render, so the object identity is stable and react-markdown doesn't
// re-create the whole element map on every keystroke.
//
// Notes on the classes:
//  - `whitespace-pre-wrap` on <p> keeps the pre-markdown behaviour where a
//    single newline inside a paragraph shows as a line break; blank-line
//    separation (e.g. the backend's new preamble paragraph break) becomes a
//    real second <p>, spaced by the wrapper's `space-y-4`.
//  - Tailwind's preflight zeroes margins on p/ul/ol/h*, so vertical rhythm is
//    owned entirely by the wrapper's `space-y-4` — nothing double-spaces.
//  - This project's Tailwind config replaces (rather than extends) `theme.
//    fontFamily`, so there is no `font-mono` utility; preflight already gives
//    <code>/<pre> the default monospace stack, which is what we rely on.
const assistantMarkdownComponents: Components = {
    p: ({ children }) => <p className="whitespace-pre-wrap leading-7">{children}</p>,
    strong: ({ children }) => <strong className="font-bold">{children}</strong>,
    em: ({ children }) => <em className="italic">{children}</em>,
    ul: ({ children }) => <ul className="list-disc space-y-1 pl-5">{children}</ul>,
    ol: ({ children }) => <ol className="list-decimal space-y-1 pl-5">{children}</ol>,
    li: ({ children }) => <li className="leading-7 [&>ol]:mt-1 [&>ul]:mt-1">{children}</li>,
    // A parameter/value table. The bubble is narrow and a datasheet table is
    // not, so the table scrolls INSIDE its own wrapper (`overflow-x-auto` on a
    // block that is allowed to be wider than the text) rather than pushing the
    // conversation column sideways.
    //
    // None of the cell overrides spread the node's own props, so the
    // `style="text-align:…"` that mdast-util-gfm-table's alignment row would
    // otherwise produce never reaches the DOM: no inline style on this page is
    // derived from answer text.
    table: ({ children }) => (
        <div className="overflow-x-auto">
            <table className="min-w-full border-collapse text-left text-sm">{children}</table>
        </div>
    ),
    thead: ({ children }) => <thead className="border-b border-primary_one-100">{children}</thead>,
    tbody: ({ children }) => <tbody>{children}</tbody>,
    tr: ({ children }) => <tr className="border-b border-white/10 last:border-b-0">{children}</tr>,
    th: ({ children }) => <th className="px-2 py-1.5 font-bold leading-6">{children}</th>,
    td: ({ children }) => <td className="px-2 py-1.5 align-top leading-6">{children}</td>,
    // Markdown headings are DEMOTED two levels (h1 -> h3, h2 -> h4, the rest
    // clamp to h5/h6) so an answer's headings can never outrank the page's own
    // outline — the chat section itself is introduced by an h2. The visual
    // classes are unchanged; only the semantic level shifts.
    h1: ({ children }) => <h3 className="text-lg font-bold leading-7">{children}</h3>,
    h2: ({ children }) => <h4 className="text-base font-bold leading-7">{children}</h4>,
    h3: ({ children }) => <h5 className="text-sm font-semibold uppercase tracking-wide">{children}</h5>,
    h4: ({ children }) => <h6 className="text-sm font-semibold">{children}</h6>,
    h5: ({ children }) => <h6 className="text-sm font-semibold">{children}</h6>,
    h6: ({ children }) => <h6 className="text-sm font-semibold">{children}</h6>,
    // A markdown image would be live DOM firing an outbound GET to whatever
    // URL the markdown names, at render time, without a click — a tracking
    // pixel or query-string exfiltration channel for content echoed out of an
    // uploaded document. The alt text is kept as inert text; nothing is
    // fetched. (Raw-HTML <img> never reaches here — see AssistantMarkdown.)
    img: ({ alt }) => <span className="italic opacity-70">{alt ? `[image: ${alt}]` : "[image]"}</span>,
    // With `whitespace-pre-wrap` on <p>, the literal newline that follows a
    // markdown hard break already renders as the line break; emitting the
    // <br> too would double it.
    br: () => null,
    blockquote: ({ children }) => (
        <blockquote className="border-l-2 border-primary_one-100 pl-3 italic opacity-90">{children}</blockquote>
    ),
    hr: () => <hr className="border-primary_one-100" />,
    // Inline code. Block code (```) arrives as <pre><code>, and the <pre>
    // override below neutralises the chip styling for that case.
    code: ({ children }) => (
        <code className="rounded bg-black/30 px-1.5 py-0.5 text-[0.9em]">{children}</code>
    ),
    pre: ({ children }) => (
        <pre className="overflow-x-auto rounded-md bg-black/30 p-3 text-sm leading-6 [&>code]:bg-transparent [&>code]:p-0 [&>code]:text-[1em]">
            {children}
        </pre>
    ),
    a: ({ children, href }) => (
        <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="break-words text-primary_one-200 underline underline-offset-2 hover:text-red-300"
        >
            {children}
        </a>
    ),
};

// Renders an ASSISTANT answer as markdown.
//
// SECURITY: raw HTML is never rendered as HTML. react-markdown drops embedded
// HTML nodes by default, and we deliberately do NOT add `rehype-raw` (or any
// `dangerouslySetInnerHTML`) — so raw markup echoed out of an uploaded
// document cannot become live DOM (no <script>, no <img onerror=...>, no
// injected event handlers). Markdown's OWN syntax is a separate channel with
// its own guards: image syntax is neutralised in the component map above
// (rendered as inert text, never fetched), links get rel="noopener noreferrer",
// and the only GFM construct enabled is the table — a bare URL sitting in
// answer text is still plain text, because autolink literals were never turned
// on. Do not add rehype-raw here, and do not swap the table plugin for
// remark-gfm.
//
// `break-words` keeps a long unbroken token (a URL, a base64 blob) from
// pushing the bubble past its max width.
function AssistantMarkdown({ text }: { text: string }) {
    return (
        <div className="space-y-4 break-words leading-7">
            <ReactMarkdown components={assistantMarkdownComponents} remarkPlugins={assistantRemarkPlugins}>
                {text}
            </ReactMarkdown>
        </div>
    );
}

export default function DocumentChatPage() {
    const navigate = useNavigate();
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const messageListRef = useRef<HTMLDivElement | null>(null);
    // Bumped every time the session is reset (expiry only — a manual "Clear
    // chat" already can't race in-flight work because it disables the upload
    // input and send button while it's pending). In-flight requests capture
    // this value before their await and compare it after; a mismatch means
    // the session they started under is dead and their result must not be
    // applied to the UI. A ref (not state) so the check is synchronous and
    // doesn't wait for a re-render.
    const sessionEpochRef = useRef(0);
    const [conversationSession, setConversationSession] = useState<ConversationSession>(() => getInitialConversationSession());
    const [messages, setMessages] = useState<ChatMessage[]>(() => [createWelcomeMessage()]);
    const [draftMessage, setDraftMessage] = useState("");
    const [isSending, setIsSending] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [isClearing, setIsClearing] = useState(false);
    const [uploadedDocuments, setUploadedDocuments] = useState<UploadedDocumentEntry[]>(() =>
        getInitialUploadedDocuments(conversationSession.conversationId));
    // The source-preview drawer: null when closed, otherwise the cited
    // document being previewed (with only its own citations).
    const [previewDocument, setPreviewDocument] = useState<PreviewDocument | null>(null);

    const conversationId = conversationSession.conversationId;
    const activeUploadEntry = uploadedDocuments.find((entry) => entry.status === "uploading");
    const hasFailedUploads = uploadedDocuments.some((entry) => entry.status === "failed");
    // A PRIMITIVE, not the array itself: used as an effect dependency below so
    // the poll's interval is created once when this flips false -> true and
    // torn down when it flips back, rather than being recreated on every
    // single reconciliation the poll itself performs.
    const hasPendingIngestionJobs = uploadedDocuments.some((entry) => entry.status === "queued" || entry.status === "indexing");

    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }, []);

    // Keep the sidebar aligned with the conversation session across a page
    // reload. The conversation id already lives in sessionStorage; storing
    // the panel under that same id prevents stale documents from leaking
    // into a newly-created or explicitly-cleared chat.
    useEffect(() => {
        persistUploadedDocuments(conversationId, uploadedDocuments);
    }, [conversationId, uploadedDocuments]);

    useEffect(() => {
        const messageList = messageListRef.current;
        // Guard for environments without Element.scrollTo (e.g. jsdom in tests,
        // some embedded webviews) so the effect can't throw and break rendering.
        if (messageList && typeof messageList.scrollTo === "function") {
            messageList.scrollTo({ top: messageList.scrollHeight, behavior: "smooth" });
        }
    }, [messages, isSending]);

    // Resets to a brand-new session: fresh conversationId, a visible notice
    // in the transcript, and the uploaded-documents panel cleared, since any
    // previously uploaded documents no longer belong to the active session.
    // The draft is deliberately left alone — a background expiry must not
    // erase a message the user is mid-composing, and the send-guard path
    // (which passes its own notice) explicitly relies on the draft surviving
    // so the user can retry after re-uploading.
    const expireSession = useCallback((noticeMessage: ChatMessage = createSessionExpiredMessage()) => {
        sessionEpochRef.current += 1;
        setConversationSession(createConversationSession());
        setMessages([noticeMessage]);
        setUploadedDocuments([]);
        // The drawer previews a document of the dead session; any preview
        // fetch still in flight is discarded by the epoch bump above.
        setPreviewDocument(null);
    }, []);

    useEffect(() => {
        const expiresIn = Math.max(0, CONVERSATION_SESSION_TTL_MS - (Date.now() - conversationSession.createdAt));
        const timer = window.setTimeout(expireSession, expiresIn);

        return () => window.clearTimeout(timer);
    }, [conversationSession, expireSession]);

    // ONE interval for the whole panel, alive only while at least one entry is
    // "queued"/"indexing" — not one poll per file. It starts the moment
    // hasPendingIngestionJobs first becomes true and is torn down the moment
    // it goes back to false (including by the effect's own cleanup on
    // unmount), so there is never more than one of these running.
    //
    // pollEpoch is captured once, when the interval is created, not read fresh
    // on every tick — it is the epoch this poll's requests belong to. Both the
    // background expiry timer above and a manual clear bump sessionEpochRef
    // BEFORE anything else they do, and clearing specifically has an async gap
    // (it awaits the backend before it ever touches uploadedDocuments) during
    // which a tick can start with a conversationId that is about to stop being
    // valid. Every tick re-checks the epoch — both before issuing the request
    // and again after it resolves — and abandons rather than reconciles the
    // instant it no longer matches, exactly like every other in-flight
    // operation in this component.
    useEffect(() => {
        if (!hasPendingIngestionJobs) return;

        const pollEpoch = sessionEpochRef.current;
        const pollConversationId = conversationId;

        const abandonPendingEntries = () => {
            setUploadedDocuments((current) => current.map((entry): UploadedDocumentEntry =>
                (entry.status === "queued" || entry.status === "indexing")
                    ? { id: entry.id, fileName: entry.fileName, status: "failed", errorMessage: STALE_UPLOAD_MESSAGE }
                    : entry));
        };

        const reconcileWithJobs = (jobs: IngestionJob[]) => {
            const jobsById = new Map(jobs.map((job) => [job.jobId, job]));

            setUploadedDocuments((current) => current.map((entry): UploadedDocumentEntry => {
                if (entry.status !== "queued" && entry.status !== "indexing") return entry;

                const job = jobsById.get(entry.jobId);
                // Retention swept this row before the poll caught up with it —
                // leave the entry exactly as it was; there is nothing further
                // this poll can ever learn about it.
                if (!job) return entry;

                switch (job.status) {
                    case "queued":
                        return entry;
                    case "running":
                        // Rebuilt every tick, not just on the queued->indexing
                        // transition: the progress fields below are the
                        // reason this case runs the poll's whole lifetime,
                        // and a document read spends most of its time here
                        // — the entry has to keep picking up new
                        // stage/done/total on every successive poll for the
                        // status line to move.
                        return {
                            id: entry.id,
                            fileName: entry.fileName,
                            status: "indexing",
                            jobId: entry.jobId,
                            progressStage: job.progressStage,
                            progressDone: job.progressDone,
                            progressTotal: job.progressTotal,
                        };
                    case "indexed":
                        // The status transition IS the once-only guard: this branch
                        // only ever runs for an entry still "queued"/"indexing", and
                        // the update below moves it out of both, so a later tick can
                        // never re-fire this toast for the same job.
                        toast.success(`${entry.fileName} is ready for questions.`, toastifyConfigs as any);
                        return { id: entry.id, fileName: entry.fileName, status: "indexed", chunkCount: job.chunkCount, readingMode: job.readingMode, jobId: entry.jobId };
                    case "failed": {
                        const message = job.errorMessage || "The document could not be indexed.";
                        toast.error(`${entry.fileName}: ${message}`, errorToastConfig as any);
                        return { id: entry.id, fileName: entry.fileName, status: "failed", errorMessage: message, jobId: entry.jobId };
                    }
                    case "superseded":
                        // The durable form of "this belongs to a conversation that
                        // was cleared" — same message as every other staleness path,
                        // and deliberately no toast: the user already knows they
                        // cleared the chat.
                        return { id: entry.id, fileName: entry.fileName, status: "failed", errorMessage: STALE_UPLOAD_MESSAGE, jobId: entry.jobId };
                    default:
                        return entry;
                }
            }));
        };

        const tick = async () => {
            if (sessionEpochRef.current !== pollEpoch) {
                window.clearInterval(intervalId);
                abandonPendingEntries();
                return;
            }

            try {
                const jobs = await fetchIngestionStatus(pollConversationId);

                if (sessionEpochRef.current !== pollEpoch) {
                    window.clearInterval(intervalId);
                    abandonPendingEntries();
                    return;
                }

                reconcileWithJobs(jobs);
            } catch {
                // Transient network error — silently retried on the next tick.
                // An entry must never be failed just because one poll couldn't
                // reach the backend.
            }
        };

        const intervalId = window.setInterval(tick, INGESTION_POLL_INTERVAL_MS);
        return () => window.clearInterval(intervalId);
    }, [hasPendingIngestionJobs, conversationId]);

    const updateUploadEntry = (id: string, entry: UploadedDocumentEntry) => {
        setUploadedDocuments((current) => current.map((item) => (item.id === id ? entry : item)));
    };

    const handleDismissUpload = (id: string) => {
        setUploadedDocuments((current) => current.filter((item) => item.id !== id));
    };

    const handleClearFailedUploads = () => {
        setUploadedDocuments((current) => current.filter((item) => item.status !== "failed"));
    };

    // Moves one large file through the initiate/parts/complete transport
    // instead of the legacy single-request path. Returns the SAME shape
    // uploadKnowledgeDocument's async contract does (jobId + status
    // "queued"), so handleFileChange's existing queued/indexing handling
    // needs no branch of its own for this path — or throws, exactly like
    // uploadKnowledgeDocument, so the SAME rate-limit-retry and generic
    // failure handling in handleFileChange's catch block covers a 429 from
    // initiate or complete without a second copy of that logic.
    //
    // The one thing it never does is throw for a stale epoch or an exhausted
    // part retry: both are terminal by construction, and both settle the
    // panel entry themselves (best-effort abort + a failed entry, with or
    // without a toast per the same staleness convention every other epoch
    // check in this file already follows) rather than hand the caller a
    // result it would apply a second, conflicting update from. A null
    // return is that settlement's own signal: stop, nothing is left to do
    // for this file.
    const performDirectUpload = async (
        file: File,
        entryId: string,
        batchEpoch: number,
        conversationId: string,
    ): Promise<UploadResponse | null> => {
        const isStale = () => sessionEpochRef.current !== batchEpoch;
        const settleAsStale = async (uploadId?: string) => {
            if (uploadId) await abortDirectUpload(uploadId).catch(() => {});
            updateUploadEntry(entryId, { id: entryId, fileName: file.name, status: "failed", errorMessage: STALE_UPLOAD_MESSAGE });
        };

        // Before initiate: the freshness check the caller already made
        // before pushing this file's "uploading" entry can be stale by the
        // time this runs, on a retry that followed a rate-limit wait.
        if (isStale()) {
            await settleAsStale();
            return null;
        }

        const initiated = await initiateDirectUpload(conversationId, file.name, file.size);

        if (isStale()) {
            await settleAsStale(initiated.uploadId);
            return null;
        }

        // Null on the local provider (parts go through this API's own
        // route instead); one presigned destination per part on S3.
        const partUrlsByNumber = new Map((initiated.partUrls ?? []).map((part) => [part.partNumber, part.url]));

        let partsDone = 0;
        updateUploadEntry(entryId, {
            id: entryId, fileName: file.name, status: "uploading", partsDone, partsTotal: initiated.partCount,
        });

        const uploadOnePart = async (partNumber: number): Promise<boolean> => {
            const start = (partNumber - 1) * initiated.partSizeBytes;
            const end = Math.min(start + initiated.partSizeBytes, file.size);
            const chunk = file.slice(start, end);
            const partUrl = partUrlsByNumber.get(partNumber) ?? null;

            for (let attempt = 0; attempt <= MAX_DIRECT_UPLOAD_PART_RETRIES; attempt += 1) {
                try {
                    await uploadDirectPart(initiated.uploadId, partNumber, partUrl, chunk);
                    return true;
                } catch {
                    if (attempt === MAX_DIRECT_UPLOAD_PART_RETRIES) return false;
                }
            }
            return false;
        };

        for (let waveStart = 1; waveStart <= initiated.partCount; waveStart += DIRECT_UPLOAD_PART_CONCURRENCY) {
            // Between part waves: a session that expires mid-transfer must
            // stop sending parts for it rather than run to completion under
            // a conversationId the backend has already discarded.
            if (isStale()) {
                await settleAsStale(initiated.uploadId);
                return null;
            }

            const waveNumbers: number[] = [];
            for (
                let partNumber = waveStart;
                partNumber < waveStart + DIRECT_UPLOAD_PART_CONCURRENCY && partNumber <= initiated.partCount;
                partNumber += 1
            ) {
                waveNumbers.push(partNumber);
            }

            const waveResults = await Promise.all(waveNumbers.map((partNumber) => uploadOnePart(partNumber)));

            if (waveResults.some((succeeded) => !succeeded)) {
                await abortDirectUpload(initiated.uploadId).catch(() => {});
                const message = "This file could not be uploaded. Please try again.";
                updateUploadEntry(entryId, { id: entryId, fileName: file.name, status: "failed", errorMessage: message });
                toast.error(`${file.name}: ${message}`, errorToastConfig as any);
                return null;
            }

            partsDone += waveNumbers.length;
            updateUploadEntry(entryId, {
                id: entryId, fileName: file.name, status: "uploading", partsDone, partsTotal: initiated.partCount,
            });
        }

        // Before complete: the last checkpoint before the transfer hands
        // over to the ingestion queue for good.
        if (isStale()) {
            await settleAsStale(initiated.uploadId);
            return null;
        }

        return completeDirectUpload(initiated.uploadId);
    };

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(event.target.files || []);
        if (!files.length) return;

        // Snapshot which session this batch belongs to. The files upload
        // sequentially, and the 2-hour expiry timer can fire between any two
        // of them (or while one is awaiting the network) — every file that
        // started after that point must not be sent under, or credited to, a
        // conversationId the backend has already discarded.
        const batchEpoch = sessionEpochRef.current;

        try {
            setIsUploading(true);

            for (const file of files) {
                const entryId = createGuid();

                if (sessionEpochRef.current !== batchEpoch) {
                    // The session expired before this file's turn came up — never
                    // send it under the dead conversationId.
                    setUploadedDocuments((current) => [...current, { id: entryId, fileName: file.name, status: "failed", errorMessage: STALE_UPLOAD_MESSAGE }]);
                    continue;
                }

                setUploadedDocuments((current) => [...current, { id: entryId, fileName: file.name, status: "uploading" }]);

                const extension = file.name.includes(".") ? `.${file.name.split(".").pop()!.toLowerCase()}` : "";
                if (!DOCUMENT_EXTENSIONS.includes(extension) || file.size > MAX_DOCUMENT_FILE_BYTES) {
                    const message = file.size > MAX_DOCUMENT_FILE_BYTES
                        ? "The file is larger than the 2 GB limit."
                        : "This file type is not supported.";
                    updateUploadEntry(entryId, { id: entryId, fileName: file.name, status: "failed", errorMessage: message });
                    toast.error(`${file.name}: ${message}`, errorToastConfig as any);
                    continue;
                }

                // Retries a 429 on THIS file in place — the outer for-loop only
                // advances to the next file once this one settles (indexed,
                // failed, or stale). The entry stays "uploading" for the whole
                // stretch; nothing here touches its status until settlement.
                let rateLimitRetries = 0;

                while (true) {
                    try {
                        const result = file.size > DIRECT_UPLOAD_THRESHOLD_BYTES
                            ? await performDirectUpload(file, entryId, batchEpoch, conversationId)
                            : await uploadKnowledgeDocument(file, conversationId);

                        if (result === null) {
                            // performDirectUpload already settled this entry itself (a stale epoch or
                            // an exhausted part retry) — nothing left for this loop to do.
                            break;
                        }

                        if (sessionEpochRef.current !== batchEpoch) {
                            // Expired while this upload was in flight. The backend may
                            // have indexed it, but it belongs to a conversation the
                            // client has already torn down — surface it as failed (if
                            // its entry is even still present) and never fire the
                            // "ready for questions" success toast for a dead session.
                            updateUploadEntry(entryId, { id: entryId, fileName: file.name, status: "failed", errorMessage: STALE_UPLOAD_MESSAGE });
                            break;
                        }

                        if (result.jobId) {
                            // Current (async) contract: accepted, not yet readable. No
                            // success toast here — it fires once, from the poll loop
                            // below, when this job's status actually reaches "indexed".
                            updateUploadEntry(entryId, { id: entryId, fileName: file.name, status: "queued", jobId: result.jobId });
                        } else if (result.indexed === true) {
                            // Legacy contract: an older deployed backend that indexed
                            // synchronously and never returned a jobId at all.
                            updateUploadEntry(entryId, {
                                id: entryId,
                                fileName: file.name,
                                status: "indexed",
                                chunkCount: typeof result.chunkCount === "number" ? result.chunkCount : null,
                                readingMode: result.readingMode,
                            });
                            toast.success(`${file.name} is ready for questions.`, toastifyConfigs as any);
                        } else {
                            // Treat a 200 where indexed isn't exactly true as a failure too —
                            // guards against an older deployed backend that returns success
                            // with a partial/failed indexing state instead of an error status.
                            const message = result.indexingError || "The document could not be indexed.";
                            updateUploadEntry(entryId, { id: entryId, fileName: file.name, status: "failed", errorMessage: message });
                            toast.error(`${file.name}: ${message}`, errorToastConfig as any);
                        }
                        break;
                    } catch (error) {
                        if (sessionEpochRef.current !== batchEpoch) {
                            updateUploadEntry(entryId, { id: entryId, fileName: file.name, status: "failed", errorMessage: STALE_UPLOAD_MESSAGE });
                            break;
                        }

                        const retryAfterSeconds = getRateLimitRetryAfterSeconds(error);
                        if (retryAfterSeconds !== null && rateLimitRetries < MAX_UPLOAD_RATE_LIMIT_RETRIES) {
                            rateLimitRetries += 1;
                            const waitSeconds = Math.min(Math.max(retryAfterSeconds, 0), MAX_UPLOAD_RATE_LIMIT_WAIT_SECONDS);
                            await wait(waitSeconds * 1000);

                            // Same staleness check as after every other await in this
                            // loop — the wait is exactly the kind of gap the 2-hour
                            // expiry timer can fire inside of.
                            if (sessionEpochRef.current !== batchEpoch) {
                                updateUploadEntry(entryId, { id: entryId, fileName: file.name, status: "failed", errorMessage: STALE_UPLOAD_MESSAGE });
                                break;
                            }

                            continue;
                        }

                        const message = getErrorMessage(error, "Upload failed.");
                        updateUploadEntry(entryId, { id: entryId, fileName: file.name, status: "failed", errorMessage: message });
                        toast.error(`${file.name}: ${message}`, errorToastConfig as any);
                        break;
                    }
                }
            }

        } finally {
            setIsUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    // Opens (or updates in place) the source-preview drawer for one cited
    // document, carrying only that document's citations.
    const openPreviewForDocument = useCallback((documentId: string, citations: SourceCitation[]) => {
        const documentCitations = citations.filter((citation) => citation.documentId === documentId);
        if (documentCitations.length === 0) return;
        setPreviewDocument({
            documentId,
            documentTitle: documentCitations[0].documentTitle,
            documentFileName: documentCitations[0].documentFileName ?? null,
            citations: documentCitations,
        });
    }, []);

    const handleSendMessage = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const messageText = draftMessage.trim();
        if (!messageText || isSending) return;

        if (isSessionExpired(conversationSession)) {
            // Preserve the draft (expireSession no longer clears it) and show a
            // notice that explicitly says the message was NOT sent, instead of
            // the generic background-expiry notice.
            expireSession(createSessionExpiredBeforeSendMessage());
            return;
        }

        const sendEpoch = sessionEpochRef.current;

        setMessages((current) => [...current, {
            id: createGuid(),
            role: "user",
            text: messageText,
            createdAt: new Date(),
        }]);
        setDraftMessage("");

        try {
            setIsSending(true);
            const result = await sendChatMessage(messageText, conversationId);

            if (sessionEpochRef.current !== sendEpoch) {
                // The session expired while this reply was in flight. The
                // transcript was already reset with its own expiry notice —
                // appending this reply now would attach a dead conversation's
                // answer to a question that's no longer visible. Discard it
                // (and, since the message is never added, its sources/feedback
                // UI never renders either).
                return;
            }

            // Older deployed backends omit additive evidence fields — undefined means [].
            const citations = result.citations || [];
            const visualAssets = result.visualAssets || [];

            setMessages((current) => [...current, {
                id: createGuid(),
                role: "assistant",
                text: result.answer,
                createdAt: new Date(),
                messageId: result.messageId,
                sources: result.sources || [],
                actions: result.actions || [],
                citations,
                visualAssets,
                escalationRecommended: result.escalationRecommended,
            }]);

            // Non-empty citations are the signal that this turn genuinely
            // answered from the documents — pull up the source preview for
            // the top document automatically (updating in place if the
            // drawer is already open). Refusals/clarifications carry no
            // citations and leave the drawer alone.
            if (citations.length > 0) {
                openPreviewForDocument(citations[0].documentId, citations);
            }
        } catch (error) {
            if (sessionEpochRef.current !== sendEpoch) {
                return;
            }

            const message = getErrorMessage(error, "Unable to send the message.");
            setMessages((current) => [...current, {
                id: createGuid(),
                role: "assistant",
                text: message,
                createdAt: new Date(),
            }]);
            toast.error(message, errorToastConfig as any);
        } finally {
            setIsSending(false);
        }
    };

    const handleFeedback = async (localMessageId: string, backendMessageId: string, rating: FeedbackRating) => {
        try {
            await sendMessageFeedback(backendMessageId, conversationId, rating);
            setMessages((current) => current.map((message) =>
                message.id === localMessageId ? { ...message, feedback: rating } : message));
            toast.success("Thanks for the feedback.", toastifyConfigs as any);
        } catch (error) {
            toast.error(getErrorMessage(error, "Unable to save feedback."), errorToastConfig as any);
        }
    };

    const handleClearConversation = async () => {
        // Bumped BEFORE the await, not after. Disabling the buttons stops work the user could START
        // during a clear, but it does nothing about work already in flight whose fetch has since aborted
        // at its own timeout while the server kept going. From this line on, any earlier upload or send
        // that resolves finds a changed epoch and discards its result instead of applying it to the new,
        // deliberately empty session.
        sessionEpochRef.current += 1;
        // The previewed document is about to be deleted server-side; close
        // the drawer now rather than leaving it over a cleared session.
        setPreviewDocument(null);

        try {
            setIsClearing(true);
            const result = await clearKnowledgeSession(conversationId);
            setConversationSession(createConversationSession(result.conversationId));
            toast.success(result.message, toastifyConfigs as any);
        } catch (error) {
            toast.error(getErrorMessage(error, "Unable to clear this chat."), errorToastConfig as any);
            return;
        } finally {
            setIsClearing(false);
        }

        setMessages([createWelcomeMessage()]);
        setDraftMessage("");
        setUploadedDocuments([]);
    };

    return (
        <div className="qouda-home min-h-screen bg-primary_one-150 text-primary_one-50">
            <Header />
            <main className="mx-auto max-w-screen-xl px-4 pb-10 pt-4 md:pb-16 md:pt-8">
                <div className="mb-8 grid items-start gap-6 lg:grid-cols-[380px_1fr]">
                    <BoxAnimation>
                        <aside className="flex flex-col overflow-hidden rounded-2xl border border-primary_one-100 bg-primary_one-140 p-7 shadow-xl lg:h-[calc(100vh-155px)] lg:min-h-[450px] lg:max-h-[720px]">
                            <div className="mb-4 flex-shrink-0">
                                <label className="mb-3 block text-[20px] font-semibold" htmlFor="document-upload">Add knowledge</label>
                                <input
                                    ref={fileInputRef}
                                    id="document-upload"
                                    type="file"
                                    accept={DOCUMENT_FILE_ACCEPT}
                                    multiple
                                    onChange={handleFileChange}
                                    disabled={isUploading || isClearing}
                                    className="block w-full rounded-md border border-primary_one-100 bg-primary_one-150 px-4 py-3 text-sm text-gray-300 file:mr-4 file:rounded-md file:border-0 file:bg-primary_one-200 file:px-4 file:py-2 file:font-semibold file:text-white disabled:cursor-not-allowed disabled:opacity-60"
                                />

                                {activeUploadEntry && activeUploadEntry.status === "uploading" && (
                                    <div role="status" className="mt-3 flex items-center gap-2 rounded-md border border-primary_one-100 bg-primary_one-150 px-3 py-2 text-xs text-gray-300">
                                        <FaSpinner className="flex-shrink-0 animate-spin text-primary_one-200" aria-hidden="true" />
                                        <span className="truncate">
                                            {typeof activeUploadEntry.partsTotal === "number" && activeUploadEntry.partsTotal > 0
                                                ? `Uploading ${activeUploadEntry.fileName}... ${Math.round(((activeUploadEntry.partsDone ?? 0) / activeUploadEntry.partsTotal) * 100)}%`
                                                : `Uploading ${activeUploadEntry.fileName}…`}
                                        </span>
                                    </div>
                                )}
                            </div>

                            <div className="flex min-h-0 flex-1 flex-col">
                                <div className="mb-2 flex flex-shrink-0 items-center justify-between gap-2">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Uploaded documents</p>
                                    {hasFailedUploads && (
                                        <button
                                            type="button"
                                            onClick={handleClearFailedUploads}
                                            className="text-xs font-semibold text-primary_one-200 hover:underline"
                                        >
                                            Clear failed
                                        </button>
                                    )}
                                </div>

                                {uploadedDocuments.length === 0 ? (
                                    <p className="text-xs text-gray-500">No documents uploaded yet.</p>
                                ) : (
                                    <ul aria-live="polite" className="max-h-64 min-h-0 flex-1 space-y-2 overflow-y-auto pr-1 lg:max-h-none">
                                        {uploadedDocuments.map((entry) => (
                                            <li
                                                key={entry.id}
                                                className={`rounded-md border px-3 py-2 text-xs ${entry.status === "failed"
                                                    ? "border-red-900/60 bg-red-950/20"
                                                    : "border-primary_one-100 bg-primary_one-150"}`}
                                            >
                                                <div className="flex items-center justify-between gap-2">
                                                    <span className="truncate font-medium text-gray-200" title={entry.fileName}>
                                                        {entry.fileName}
                                                    </span>
                                                    <span className="flex flex-shrink-0 items-center gap-1.5">
                                                        {(entry.status === "uploading" || entry.status === "queued" || entry.status === "indexing") && (
                                                            <FaSpinner className="animate-spin text-gray-400" aria-hidden="true" title="Uploading" />
                                                        )}
                                                        {entry.status === "indexed" && (
                                                            <FaCircleCheck className="text-emerald-400" aria-hidden="true" title="Indexed" />
                                                        )}
                                                        {entry.status === "failed" && (
                                                            <FaTriangleExclamation className="text-red-400" aria-hidden="true" title="Failed" />
                                                        )}
                                                        {entry.status === "indexed" || entry.status === "failed" ? (
                                                            <button
                                                                type="button"
                                                                onClick={() => handleDismissUpload(entry.id)}
                                                                aria-label={`Dismiss ${entry.fileName}`}
                                                                className="rounded p-0.5 text-gray-500 transition-colors hover:text-gray-300"
                                                            >
                                                                <FaXmark aria-hidden="true" />
                                                            </button>
                                                        ) : null}
                                                    </span>
                                                </div>

                                                {entry.status === "uploading" && typeof entry.partsTotal === "number" && entry.partsTotal > 0 && (
                                                    <p className="mt-1 text-gray-400">
                                                        Uploading... {Math.round(((entry.partsDone ?? 0) / entry.partsTotal) * 100)}%
                                                    </p>
                                                )}
                                                {entry.status === "queued" && (
                                                    <p className="mt-1 text-gray-400">Waiting to be read...</p>
                                                )}
                                                {entry.status === "indexing" && (
                                                    <p className="mt-1 text-gray-400">
                                                        {describeIndexingProgress(entry.progressStage, entry.progressDone, entry.progressTotal)}
                                                    </p>
                                                )}
                                                {entry.status === "indexed" && (
                                                    <p className="mt-1 text-gray-400">
                                                        Indexed{entry.chunkCount != null ? ` · ${entry.chunkCount} chunk${entry.chunkCount === 1 ? "" : "s"}` : ""}
                                                    </p>
                                                )}
                                                {entry.status === "indexed" && entry.readingMode === "text_only" && (
                                                    // A warning, not an error: the upload succeeded and the document is
                                                    // usable, but its page images (charts, scanned figures) were never
                                                    // read, so an answer drawn from this document can miss whatever
                                                    // only exists as a picture on the page.
                                                    <p className="mt-1 text-amber-300">
                                                        Indexed from text only — charts and scanned images on the pages were not read. Larger PDFs get a better reading when split into smaller files.
                                                    </p>
                                                )}
                                                {entry.status === "failed" && (
                                                    <p className="mt-1 text-red-300">{entry.errorMessage}</p>
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>

                            <button
                                type="button"
                                onClick={handleClearConversation}
                                disabled={isClearing || isSending || isUploading}
                                className="mt-4 w-full flex-shrink-0 rounded-md border border-primary_one-200 px-6 py-3 font-semibold uppercase tracking-wide text-primary_one-200 transition-colors hover:bg-primary_one-200 hover:text-white disabled:cursor-not-allowed disabled:opacity-60"
                            >
                                {isClearing ? "Clearing..." : "Clear chat"}
                            </button>
                        </aside>
                    </BoxAnimation>

                    <BoxAnimation>
                        <section className="flex h-[calc(100svh-2rem)] min-h-[520px] max-h-[720px] flex-col overflow-hidden rounded-2xl border border-primary_one-100 bg-primary_one-140 p-7 shadow-xl lg:h-[calc(100vh-155px)] lg:min-h-[450px]">
                            <div className="mb-5 flex flex-shrink-0 items-center justify-between gap-4 border-b border-primary_one-100 pb-4 max-md:flex-col max-md:items-start">
                                <div>
                                    <h2 className="text-[28px] font-bold max-md:text-[24px]">Chat with Qouda</h2>
                                    <p className="mt-1 text-xs text-gray-400">Conversation session expires after two hours</p>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => navigate("/")}
                                    className="rounded-md border border-primary_one-200 px-5 py-2 text-sm font-semibold uppercase tracking-wide text-primary_one-200 transition-colors hover:bg-primary_one-200 hover:text-white"
                                >
                                    Back home
                                </button>
                            </div>

                            <div ref={messageListRef} className="min-h-0 flex-1 space-y-4 overflow-y-auto pr-2" aria-live="polite">
                                {messages.map((message) => (
                                    <article
                                        key={message.id}
                                        className={`max-w-[88%] rounded-2xl px-5 py-4 ${message.role === "user"
                                            ? "ml-auto bg-primary_one-200 text-white"
                                            : "mr-auto border border-primary_one-100 bg-primary_one-150 text-gray-200"}`}
                                    >
                                        <p className="mb-2 text-xs font-semibold uppercase tracking-wide opacity-80">
                                            {message.role === "user" ? "You" : "Qouda"}
                                        </p>
                                        {/* Only ASSISTANT answers are parsed as markdown. User-typed text is
                                            rendered verbatim so a message like "**not bold**" or "- not a bullet"
                                            is never re-interpreted as markup by the UI. */}
                                        {message.role === "assistant"
                                            ? <AssistantMarkdown text={message.text} />
                                            : <p className="whitespace-pre-wrap leading-7">{message.text}</p>}

                                        {message.visualAssets?.length ? (
                                            <div className="mt-4 space-y-3">
                                                {message.visualAssets.map((asset) => (
                                                    <InlineVisualAssetPreview
                                                        key={asset.assetId}
                                                        asset={asset}
                                                        conversationId={conversationId}
                                                    />
                                                ))}
                                            </div>
                                        ) : null}

                                        {message.citations?.length ? (
                                            // Citation-backed sources: one row per cited document, with the
                                            // pages the answer drew from and a preview trigger. Deliberately a
                                            // BUTTON, not a link — the drawer is UI state, and link semantics
                                            // inside the answer article are reserved for answer-authored URLs.
                                            <div className="mt-4 border-t border-primary_one-100 pt-3">
                                                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">Sources</p>
                                                <ul className="space-y-2 text-sm">
                                                    {groupCitationsByDocument(message.citations).map((group) => (
                                                        <li key={group.documentId} className="flex flex-wrap items-center gap-x-3 gap-y-1">
                                                            <span className="min-w-0 truncate font-medium" title={group.title}>{group.title}</span>
                                                            {group.pages.length > 0 && (
                                                                <span className="text-xs text-gray-400">
                                                                    p. {group.pages.join(", ")}
                                                                </span>
                                                            )}
                                                            <button
                                                                type="button"
                                                                onClick={() => openPreviewForDocument(group.documentId, message.citations!)}
                                                                className="rounded border border-primary_one-100 px-2 py-1 text-xs font-semibold text-primary_one-200 transition-colors hover:border-primary_one-200"
                                                            >
                                                                View source
                                                            </button>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        ) : !!message.sources?.length && (
                                            <div className="mt-4 border-t border-primary_one-100 pt-3">
                                                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">Sources</p>
                                                <ul className="space-y-1 text-sm">
                                                    {message.sources.map((source, index) => (
                                                        <li key={`${source.title}-${index}`}>
                                                            {source.url ? (
                                                                <a href={source.url} target="_blank" rel="noreferrer" className="text-primary_one-200 underline hover:text-red-300">
                                                                    {source.title}
                                                                </a>
                                                            ) : source.title}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}

                                        {message.role === "assistant" && message.messageId && (
                                            <div className="mt-4 flex items-center gap-2 border-t border-primary_one-100 pt-3 text-xs">
                                                <span className="text-gray-400">Was this helpful?</span>
                                                <button
                                                    type="button"
                                                    onClick={() => handleFeedback(message.id, message.messageId!, "helpful")}
                                                    disabled={!!message.feedback}
                                                    className="rounded border border-primary_one-100 px-2 py-1 hover:border-primary_one-200 disabled:opacity-60"
                                                >
                                                    {message.feedback === "helpful" ? "Helpful ✓" : "Helpful"}
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => handleFeedback(message.id, message.messageId!, "not_helpful")}
                                                    disabled={!!message.feedback}
                                                    className="rounded border border-primary_one-100 px-2 py-1 hover:border-primary_one-200 disabled:opacity-60"
                                                >
                                                    {message.feedback === "not_helpful" ? "Not helpful ✓" : "Not helpful"}
                                                </button>
                                            </div>
                                        )}
                                    </article>
                                ))}

                                {isSending && (
                                    <article className="mr-auto max-w-[88%] rounded-2xl border border-primary_one-100 bg-primary_one-150 px-5 py-4 text-gray-300">
                                        Qouda is checking your knowledge...
                                    </article>
                                )}
                            </div>

                            <form onSubmit={handleSendMessage} className="mt-6 flex flex-shrink-0 gap-3 max-md:flex-col">
                                <textarea
                                    value={draftMessage}
                                    onChange={(event) => setDraftMessage(event.target.value)}
                                    placeholder="Ask about this chat’s documents..."
                                    maxLength={2000}
                                    disabled={isSending || isClearing || isUploading}
                                    className="min-h-[58px] flex-1 resize-none rounded-md border border-primary_one-100 bg-primary_one-150 px-4 py-3 text-white outline-none transition-colors placeholder-gray-400 focus:border-primary_one-200 disabled:cursor-not-allowed disabled:opacity-60"
                                />
                                <button
                                    type="submit"
                                    disabled={!draftMessage.trim() || isSending || isClearing || isUploading}
                                    className="rounded-md bg-primary_one-200 px-8 py-3 font-semibold uppercase tracking-wide text-white transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
                                >
                                    {isSending ? "Sending..." : "Send"}
                                </button>
                            </form>
                        </section>
                    </BoxAnimation>
                </div>
            </main>

            <SourcePreviewDrawer
                document={previewDocument}
                conversationId={conversationId}
                sessionEpochRef={sessionEpochRef}
                onClose={() => setPreviewDocument(null)}
            />
        </div>
    );
}
